
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Shield, Star, TrendingUp, Loader2, CheckCircle, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchCreatorReputation } from "@/utils/reputationUtils";
import { verifyGitcoinPassport, getPassportScore } from "@/utils/gitcoinPassport";
import { formatDistanceToNow } from "date-fns";

interface ReputationCardProps {
  creatorAddress: string;
}

const ReputationCard = ({ creatorAddress }: ReputationCardProps) => {
  const { data: reputation, isLoading: isReputationLoading } = useQuery({
    queryKey: ['reputation', creatorAddress],
    queryFn: () => fetchCreatorReputation(creatorAddress),
    enabled: !!creatorAddress
  });

  const { data: isVerified, isLoading: isVerificationLoading } = useQuery({
    queryKey: ['gitcoin-verification', creatorAddress],
    queryFn: () => verifyGitcoinPassport(creatorAddress),
    enabled: !!creatorAddress
  });

  const { data: passportScore } = useQuery({
    queryKey: ['gitcoin-score', creatorAddress],
    queryFn: () => getPassportScore(creatorAddress),
    enabled: !!creatorAddress && isVerified === true
  });

  if (isReputationLoading || isVerificationLoading) {
    return (
      <div className="glass-card rounded-lg p-4 flex items-center justify-center h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!reputation) {
    return null;
  }

  const adjustedTrustScore = isVerified ? 
    Math.min(100, reputation.trustScore + (passportScore || 0) * 0.2) : 
    reputation.trustScore;

  return (
    <div className="glass-card rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          community creator
        </h3>
        <div className="flex items-center gap-2">
          {isVerified && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle className="h-3 w-3" />
              Gitcoin verified
            </Badge>
          )}
          {reputation.isVerified && (
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3" />
              verified creator
            </Badge>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Trust Score</span>
            <span>{Math.round(adjustedTrustScore)}/100</span>
          </div>
          <Progress value={adjustedTrustScore} className="h-1" />
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs">Previous Communities</span>
            <p className="font-medium">{reputation.previousLaunches} successful</p>
          </div>
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs">Community Growth</span>
            <p className="font-medium flex items-center gap-1 text-green-500">
              <TrendingUp className="h-3 w-3" />
              +{reputation.averageRoi}%
            </p>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          Last verified: {formatDistanceToNow(reputation.lastVerified)} ago
          {isVerified && passportScore && (
            <div className="mt-1">
              Gitcoin Passport Score: {passportScore}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReputationCard;
